/**
 * 業務 API がエラー応答 (4xx / 5xx) を返したことを示すエラー。
 *
 * `fetchWithAuth` が投げる {@link ReauthRequiredError} / {@link NetworkError} とは別物で、
 * 「リクエストは届いたが、サーバーがエラーを返した」ケースを表す。
 *
 * サーバーは全経路で `application/problem+json` を返すため（[[API設計規則]]）、`detail` に
 * ガード違反の理由が入る。これを拾わないと利用者には定型文しか出ない。
 *
 * amplify 側にも同名の型があるが共有しない（結果的共通は各ケースへ意図的複製する方針）。
 * 本実装は amplify 版と違い、**呼び出し側の文脈メッセージを fallback として保持**する
 * （`detail` が無い応答でも「何の操作が失敗したか」を落とさないため）。
 */
export class ApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = "ApiError";
        this.status = status;
    }

    /**
     * `Response` から構築する。problem+json の `detail`（無ければ `title`）が取れればそれを、
     * 取れなければ `fallback` にステータスを添えたものをメッセージにする。
     *
     * ボディは `clone()` して読むため、呼び出し側で本文を再読しても壊れない。
     */
    static async fromResponse(res: Response, fallback: string): Promise<ApiError> {
        let message = `${fallback} (status ${res.status})`;
        try {
            const body: unknown = await res.clone().json();
            if (body && typeof body === "object") {
                const rec = body as Record<string, unknown>;
                const detail = rec["detail"] ?? rec["title"];
                if (typeof detail === "string" && detail.length > 0) {
                    message = detail;
                }
            }
        } catch {
            // problem+json でない / ボディ無し。fallback を使う。
        }
        return new ApiError(res.status, message);
    }
}
